import { Close } from "@mui/icons-material";
import { Alert, IconButton, Modal, Stack, Typography } from "@mui/material";
import { FormEvent, useCallback, useState } from "react";
import { useUpdatePatientPreferredPharmacyMutation } from "src/api/services/patientPreferredPharmacyApi";
import { EPrescribePharmacy, PatientPreferredPharmacy } from "src/api/types";
import LoadingButton from "src/components/Buttons/LoadingButton";
import PharmacySearch from "src/components/PharmacySearch";
import { isError } from "src/utils/isError";
import { desktopSx } from "src/utils/styles/modalStyles";

const formattedAddress = (pharmacy: EPrescribePharmacy) => {
  return [
    pharmacy.Address1,
    pharmacy.Address2,
    pharmacy.City,
    pharmacy.State,
    pharmacy.ZipCode,
  ]
    .filter(Boolean)
    .join(", ");
};

interface Props {
  open: boolean;
  onClose: () => void;
  preferredPharmacy: PatientPreferredPharmacy;
}

const UpdatePreferredPharmacyModal = ({
  onClose,
  open,
  preferredPharmacy,
}: Props) => {
  const [updatePharmacy] = useUpdatePatientPreferredPharmacyMutation();
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [selectedPharmacy, setSelectedPharmacy] =
    useState<EPrescribePharmacy | null>(null);

  const handleClose = useCallback(() => {
    setError(null);
    setSelectedPharmacy(null);
    setLoading(false);
    onClose();
  }, []);

  const onSubmit = useCallback(
    async (e: FormEvent<HTMLFormElement>) => {
      if (selectedPharmacy == null) return null;

      e.preventDefault();

      setLoading(false);
      try {
        await updatePharmacy({
          external_pharmacy_id: String(selectedPharmacy.PharmacyId),
          name: selectedPharmacy.StoreName,
          full_address: formattedAddress(selectedPharmacy),
          patient_id: preferredPharmacy.patient_id,
        }).unwrap();

        handleClose();
      } catch (err) {
        if (isError(err)) {
          setError(err.data.message);
        }
      }
    },
    [
      handleClose,
      preferredPharmacy.patient_id,
      selectedPharmacy,
      updatePharmacy,
    ],
  );

  return (
    <Modal open={open}>
      <Stack
        sx={{ ...desktopSx, minHeight: 500 }}
        gap={3}
        component="form"
        onSubmit={onSubmit}
      >
        <Stack direction="row" justifyContent="space-between">
          <Typography fontWeight={700} variant="h5">
            Update pharmacy:
          </Typography>
          <IconButton onClick={handleClose}>
            <Close fontSize="medium" />
          </IconButton>
        </Stack>
        <Stack gap={2} overflow="scroll" maxHeight={600}>
          <Alert severity="info" sx={{ width: 400 }}>
            Results are displaying the 20 nearest pharmacies to the patient's
            zip code.
          </Alert>
          <Typography fontWeight={600}>Search pharmacy:</Typography>
          <PharmacySearch
            patientId={preferredPharmacy.patient_id}
            onSelect={setSelectedPharmacy}
          />
        </Stack>
        <Stack gap={1}>
          {error ? (
            <Typography sx={{ color: "red" }} textAlign="center">
              {error}
            </Typography>
          ) : null}
          <LoadingButton
            loading={loading}
            type="submit"
            disabled={selectedPharmacy == null}
          >
            Confirm
          </LoadingButton>
        </Stack>
      </Stack>
    </Modal>
  );
};

export default UpdatePreferredPharmacyModal;
